import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSafetyTasks, updateTaskStatus, type SafetyTask } from "@/mocks/sharedState";

type AppTab = "tasks" | "record" | "history" | "profile";

const checkItems = [
  { id: "c1", category: "燃气管道", items: ["管道无锈蚀、无泄漏", "管道固定牢固", "穿墙管道有套管保护", "管道与电线间距符合规范"] },
  { id: "c2", category: "燃气表具", items: ["表具外观完好无损", "表具读数正常", "表具安装位置合规", "表具阀门开关灵活"] },
  { id: "c3", category: "燃气灶具", items: ["灶具点火正常", "火焰燃烧稳定", "灶具与可燃物距离合规", "灶具软管无老化破损"] },
  { id: "c4", category: "通风设施", items: ["厨房通风良好", "燃气报警器正常工作", "紧急切断阀功能正常", "用气场所无违规改造"] },
];

interface CheckResult {
  [key: string]: "pass" | "fail" | "na" | null;
}

const riskColors: Record<string, string> = {
  "极高": "bg-red-50 text-red-600 border-red-200",
  "高": "bg-orange-50 text-orange-600 border-orange-200",
  "中": "bg-amber-50 text-amber-600 border-amber-200",
  "低": "bg-green-50 text-green-600 border-green-200",
};

export default function InspectorPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<AppTab>("tasks");
  const [tasks, setTasks] = useState<SafetyTask[]>([]);
  const [selectedTask, setSelectedTask] = useState<SafetyTask | null>(null);
  const [checkResults, setCheckResults] = useState<CheckResult>({});
  const [photos, setPhotos] = useState<string[]>([]);
  const [signed, setSigned] = useState(false);
  const [remark, setRemark] = useState("");
  const [submitDone, setSubmitDone] = useState(false);
  const [syncPulse, setSyncPulse] = useState(false);
  const [newTaskCount, setNewTaskCount] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState("");
  const prevTaskCountRef = useRef(0);

  // Load tasks from shared state (simulating real-time sync)
  const loadTasks = () => {
    const allTasks = getSafetyTasks();
    // Inspector "I001" = 王建军
    const myTasks = allTasks.filter(t => t.inspectorId === "I001");
    const prevCount = prevTaskCountRef.current;
    if (prevCount > 0 && myTasks.length > prevCount) {
      setNewTaskCount(myTasks.length - prevCount);
      setSyncPulse(true);
      setTimeout(() => setSyncPulse(false), 3000);
    }
    prevTaskCountRef.current = myTasks.length;
    setTasks(myTasks);
  };

  useEffect(() => {
    loadTasks();
    // Poll for new tasks every 5 seconds (simulating real-time sync)
    const interval = setInterval(loadTasks, 5000);
    return () => clearInterval(interval);
  }, []);

  const pendingTasks = tasks.filter(t => t.status === "待安检");
  const completedTasks = tasks.filter(t => t.status === "已完成");

  const totalItems = checkItems.reduce((s, c) => s + c.items.length, 0);
  const passCount = Object.values(checkResults).filter(v => v === "pass").length;
  const failCount = Object.values(checkResults).filter(v => v === "fail").length;
  const score = totalItems > 0 ? Math.round(((totalItems - failCount) / totalItems) * 100) : 0;

  const setResult = (key: string, val: "pass" | "fail" | "na") => {
    setCheckResults(prev => ({ ...prev, [key]: val }));
  };

  const addMockPhoto = () => {
    const mockUrls = [
      "https://public.readdy.ai/ai/img_res/26d482735492747cccf03283eb1d6fcf.jpg",
      "https://readdy.ai/api/search-image?query=gas%20pipe%20connection%20valve%20indoor%20safety%20check%20professional%20photo%20clean%20background&width=200&height=200&seq=photo2&orientation=squarish",
      "https://readdy.ai/api/search-image?query=gas%20stove%20burner%20kitchen%20safety%20inspection%20professional%20photo%20clean%20background&width=200&height=200&seq=photo3&orientation=squarish",
    ];
    if (photos.length < 3) {
      setPhotos(prev => [...prev, mockUrls[prev.length]]);
    }
  };

  const startDraw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
  };

  const endDraw = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL());
      setSigned(true);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
    setSignatureData("");
  };

  const handleSubmit = () => {
    if (selectedTask) {
      updateTaskStatus(selectedTask.id, "已完成");
      loadTasks();
    }
    setSubmitDone(true);
  };

  const handleStartTask = (task: SafetyTask) => {
    updateTaskStatus(task.id, "进行中");
    loadTasks();
    setSelectedTask({ ...task, status: "进行中" });
    setCheckResults({});
    setPhotos([]);
    setSigned(false);
    setSubmitDone(false);
    setRemark("");
    setSignatureData("");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-[390px] bg-white relative overflow-hidden flex flex-col" style={{ height: "844px", boxShadow: "0 0 60px rgba(0,0,0,0.15)" }}>
        {/* Status bar */}
        <div className="bg-[#0f1729] px-5 pt-3 pb-0 flex items-center justify-between flex-shrink-0">
          <span className="text-white text-xs font-medium">9:41</span>
          <div className="flex items-center gap-1">
            {syncPulse && <span className="text-green-400 text-[10px] animate-pulse">已同步</span>}
            <i className="ri-wifi-line text-white text-xs"></i>
            <i className="ri-battery-2-charge-line text-white text-xs"></i>
          </div>
        </div>

        {/* Header */}
        <div className="bg-[#0f1729] px-5 pb-4 pt-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white/60 text-xs">AI智慧燃气</div>
              <div className="text-white font-bold text-base">安检员工作台</div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`text-xs px-2 py-1 rounded-full ${syncPulse ? "bg-green-500/30 text-green-300" : "bg-green-500/20 text-green-400"}`}>
                {syncPulse ? "新任务!" : "在线"}
              </div>
              <div className="w-9 h-9 rounded-full bg-[#E8531A] flex items-center justify-center">
                <span className="text-white text-sm font-bold">王</span>
              </div>
            </div>
          </div>
          {/* Today stats */}
          <div className="grid grid-cols-3 gap-2 mt-3">
            {[
              { label: "今日任务", value: tasks.filter(t => t.scheduledDate === "2026-04-11").length, color: "text-white" },
              { label: "已完成", value: completedTasks.length, color: "text-green-400" },
              { label: "待处理", value: pendingTasks.length, color: "text-amber-400" },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 rounded-xl p-2.5 text-center">
                <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-white/50 text-[10px]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Tasks tab */}
          {tab === "tasks" && !selectedTask && (
            <div className="p-4 space-y-3">
              {/* Sync notice */}
              {newTaskCount > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                  <i className="ri-notification-3-line text-green-600"></i>
                  <span className="text-xs text-green-700 font-medium">后台新分配了 {newTaskCount} 个安检任务</span>
                  <button onClick={() => setNewTaskCount(0)} className="ml-auto text-xs text-green-500 cursor-pointer whitespace-nowrap">知道了</button>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">我的安检任务</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-xs text-gray-400">实时同步</span>
                </div>
              </div>

              {tasks.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-task-line text-gray-300 text-3xl"></i>
                  </div>
                  <div className="text-sm text-gray-400">暂无分配任务</div>
                  <div className="text-xs text-gray-300 mt-1">等待后台分配安检任务</div>
                </div>
              ) : (
                tasks.map(task => (
                  <div
                    key={task.id}
                    className={`bg-white rounded-xl border p-4 transition-colors ${
                      task.priority === "urgent" ? "border-red-200" : "border-gray-100"
                    } ${task.status === "待安检" || task.status === "进行中" ? "cursor-pointer hover:border-[#E8531A]/30" : ""}`}
                    onClick={() => {
                      if (task.status === "待安检" || task.status === "进行中") {
                        handleStartTask(task);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {task.priority === "urgent" && (
                          <span className="text-[10px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded font-medium">紧急</span>
                        )}
                        {task.riskLevel && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${riskColors[task.riskLevel] || "bg-gray-50 text-gray-500 border-gray-200"}`}>
                            {task.riskLevel}风险
                          </span>
                        )}
                        <span className="text-sm font-semibold text-gray-800">{task.userName}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                        task.status === "已完成" ? "bg-green-50 text-green-600" :
                        task.status === "进行中" ? "bg-blue-50 text-blue-600" :
                        "bg-amber-50 text-amber-600"
                      }`}>{task.status}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                      <i className="ri-map-pin-line text-gray-400"></i>
                      <span className="truncate">{task.address}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                      <span><i className="ri-time-line mr-1"></i>{task.scheduledDate} {task.scheduledTime}</span>
                      <span>{task.type}</span>
                    </div>
                    {task.assignedAt && (
                      <div className="text-[10px] text-gray-300">
                        <i className="ri-send-plane-line mr-1"></i>分配时间: {task.assignedAt}
                      </div>
                    )}
                    {(task.status === "待安检" || task.status === "进行中") && (
                      <div className="flex gap-2 mt-3">
                        <a
                          href={`tel:${task.phone}`}
                          className="flex-1 flex items-center justify-center gap-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-xs cursor-pointer whitespace-nowrap"
                          onClick={e => e.stopPropagation()}
                        >
                          <i className="ri-phone-line"></i>联系用户
                        </a>
                        <button
                          className="flex-1 flex items-center justify-center gap-1 bg-[#E8531A] text-white py-2 rounded-lg text-xs cursor-pointer whitespace-nowrap"
                          onClick={e => { e.stopPropagation(); handleStartTask(task); }}
                        >
                          <i className="ri-navigation-line"></i>
                          {task.status === "进行中" ? "继续安检" : "开始安检"}
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Inspection form */}
          {tab === "tasks" && selectedTask && (
            <div className="p-4 space-y-4">
              {submitDone ? (
                <div className="text-center py-10">
                  <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-double-line text-green-500 text-4xl"></i>
                  </div>
                  <div className="text-lg font-bold text-gray-900 mb-1">安检完成！</div>
                  <div className="text-sm text-gray-500 mb-1">安检评分: <span className={`font-bold ${score >= 90 ? "text-green-600" : score >= 70 ? "text-amber-600" : "text-red-600"}`}>{score}分</span></div>
                  <div className="text-xs text-gray-400 mb-1">报告已自动上传至系统</div>
                  <div className="text-xs text-green-500 flex items-center justify-center gap-1">
                    <i className="ri-check-line"></i>状态已同步至后台管理系统
                  </div>
                  <button
                    onClick={() => { setSelectedTask(null); setTab("tasks"); }}
                    className="mt-6 bg-[#E8531A] text-white px-6 py-2 rounded-xl text-sm cursor-pointer whitespace-nowrap"
                  >
                    返回任务列表
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSelectedTask(null)} className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
                      <i className="ri-arrow-left-line text-gray-600 text-sm"></i>
                    </button>
                    <div>
                      <div className="text-sm font-bold text-gray-900">{selectedTask.userName} - {selectedTask.type}</div>
                      <div className="text-xs text-gray-400">{selectedTask.id}</div>
                    </div>
                    {selectedTask.riskLevel && (
                      <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-medium border ${riskColors[selectedTask.riskLevel] || ""}`}>
                        {selectedTask.riskLevel}风险
                      </span>
                    )}
                  </div>

                  {/* Progress */}
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-gray-500">检查进度</span>
                      <span className="font-medium text-gray-700">{passCount + failCount}/{totalItems}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-[#E8531A] h-2 rounded-full transition-all" style={{ width: `${((passCount + failCount) / totalItems) * 100}%` }} />
                    </div>
                    <div className="flex gap-3 mt-2 text-xs">
                      <span className="text-green-600">通过 {passCount}</span>
                      <span className="text-red-500">不合格 {failCount}</span>
                      <span className="text-gray-400">未检 {totalItems - passCount - failCount}</span>
                    </div>
                  </div>

                  {/* Check items */}
                  {checkItems.map(category => (
                    <div key={category.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100">
                        <span className="text-xs font-semibold text-gray-700">{category.category}</span>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {category.items.map((item, idx) => {
                          const key = `${category.id}-${idx}`;
                          const result = checkResults[key];
                          return (
                            <div key={idx} className="px-4 py-3">
                              <div className="text-xs text-gray-700 mb-2">{item}</div>
                              <div className="flex gap-2">
                                {(["pass", "fail", "na"] as const).map(val => (
                                  <button
                                    key={val}
                                    onClick={() => setResult(key, val)}
                                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                                      result === val
                                        ? val === "pass" ? "bg-green-500 text-white border-green-500"
                                          : val === "fail" ? "bg-red-500 text-white border-red-500"
                                          : "bg-gray-500 text-white border-gray-500"
                                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                                    }`}
                                  >
                                    {val === "pass" ? "合格" : val === "fail" ? "不合格" : "不适用"}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Photo upload */}
                  <div className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-gray-700">现场拍照记录</span>
                      <span className="text-xs text-gray-400">{photos.length}/3</span>
                    </div>
                    <div className="flex gap-2">
                      {photos.map((url, i) => (
                        <div key={i} className="w-20 h-20 rounded-xl overflow-hidden relative">
                          <img src={url} alt="现场照片" className="w-full h-full object-cover" />
                          <button onClick={() => setPhotos(prev => prev.filter((_, pi) => pi !== i))} className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center cursor-pointer">
                            <i className="ri-close-line text-white text-xs"></i>
                          </button>
                        </div>
                      ))}
                      {photos.length < 3 && (
                        <button onClick={addMockPhoto} className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#E8531A]/50 transition-colors">
                          <i className="ri-camera-line text-gray-400 text-xl"></i>
                          <span className="text-[10px] text-gray-400">拍照</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Remark */}
                  <div className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="text-xs font-semibold text-gray-700 mb-2">安检备注</div>
                    <textarea rows={3} maxLength={500} placeholder="记录安检发现的问题或特殊情况..." value={remark} onChange={e => setRemark(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-[#E8531A] resize-none" />
                  </div>

                  {/* Electronic signature */}
                  <div className="bg-white rounded-xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700">用户电子签名</span>
                      {signed && <button onClick={clearSignature} className="text-xs text-gray-400 cursor-pointer">重签</button>}
                    </div>
                    <div className={`border-2 rounded-xl overflow-hidden ${signed ? "border-green-300" : "border-dashed border-gray-200"}`}>
                      <canvas
                        ref={canvasRef}
                        width={330}
                        height={100}
                        className="w-full cursor-crosshair"
                        onMouseDown={startDraw}
                        onMouseMove={draw}
                        onMouseUp={endDraw}
                        onMouseLeave={endDraw}
                      />
                    </div>
                    {!signed && <p className="text-[10px] text-gray-400 mt-1 text-center">请用户在上方区域签名确认</p>}
                    {signed && <p className="text-[10px] text-green-500 mt-1 text-center flex items-center justify-center gap-1"><i className="ri-check-line"></i>签名已完成</p>}
                  </div>

                  {/* Submit */}
                  <button
                    onClick={handleSubmit}
                    disabled={passCount + failCount < totalItems || !signed}
                    className="w-full bg-[#E8531A] text-white py-4 rounded-2xl text-base font-bold cursor-pointer hover:bg-[#d44a15] transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    提交安检报告 (评分: {score}分)
                  </button>
                </>
              )}
            </div>
          )}

          {/* History tab */}
          {tab === "history" && (
            <div className="p-4 space-y-3">
              <div className="text-sm font-semibold text-gray-800">历史安检记录</div>
              {completedTasks.length > 0 && completedTasks.map((task, i) => (
                <div key={task.id} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">{task.userName}</div>
                      <div className="text-xs text-gray-400">{task.id} · {task.scheduledDate}</div>
                    </div>
                    <div className="text-2xl font-bold text-green-600">95</div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">{task.type}</span>
                    <span className="text-green-500">已完成</span>
                  </div>
                </div>
              ))}
              {[
                { id: "SC-2026-0399", name: "陈志远", date: "2026-04-09", score: 95, type: "年度安检", issues: 0 },
                { id: "SC-2026-0395", name: "刘美华", date: "2026-04-08", score: 78, type: "整改复检", issues: 2 },
                { id: "SC-2026-0390", name: "孙建平", date: "2026-04-07", score: 100, type: "新装安检", issues: 0 },
                { id: "SC-2026-0385", name: "周晓燕", date: "2026-04-06", score: 88, type: "年度安检", issues: 1 },
                { id: "SC-2026-0380", name: "吴大勇", date: "2026-04-05", score: 65, type: "年度安检", issues: 3 },
              ].map((rec, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">{rec.name}</div>
                      <div className="text-xs text-gray-400">{rec.id} · {rec.date}</div>
                    </div>
                    <div className={`text-2xl font-bold ${rec.score >= 90 ? "text-green-600" : rec.score >= 70 ? "text-amber-600" : "text-red-600"}`}>{rec.score}</div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">{rec.type}</span>
                    {rec.issues > 0 ? (
                      <span className="text-red-500">发现 {rec.issues} 个问题</span>
                    ) : (
                      <span className="text-green-500">无问题</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Profile tab */}
          {tab === "profile" && (
            <div className="p-4 space-y-4">
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#0f1729] flex items-center justify-center">
                    <span className="text-white text-xl font-bold">王</span>
                  </div>
                  <div>
                    <div className="text-base font-bold text-gray-900">王建军</div>
                    <div className="text-xs text-gray-500">安检员 · 工号: INS-0023</div>
                    <div className="text-xs text-green-500 mt-0.5">认证安检员</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "本月完成", value: String(completedTasks.length + 47), unit: "次" },
                    { label: "平均评分", value: "92.3", unit: "分" },
                    { label: "服务年限", value: "3.5", unit: "年" },
                  ].map((s, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
                      <div className="text-lg font-bold text-gray-800">{s.value}<span className="text-xs text-gray-400">{s.unit}</span></div>
                      <div className="text-[10px] text-gray-400 mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                {[
                  { icon: "ri-calendar-check-line", label: "我的排班", sub: "查看本周安检排班" },
                  { icon: "ri-file-list-3-line", label: "安检报告", sub: "历史安检报告查询" },
                  { icon: "ri-award-line", label: "技能认证", sub: "查看资质证书" },
                  { icon: "ri-settings-3-line", label: "设置", sub: "通知与偏好设置" },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between cursor-pointer hover:border-[#E8531A]/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
                        <i className={`${item.icon} text-[#E8531A] text-base`}></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800">{item.label}</div>
                        <div className="text-xs text-gray-400">{item.sub}</div>
                      </div>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-400"></i>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom nav */}
        <div className="border-t border-gray-100 bg-white px-2 py-2 flex items-center justify-around flex-shrink-0">
          {[
            { key: "tasks", icon: "ri-task-line", label: "任务", badge: pendingTasks.length },
            { key: "record", icon: "ri-edit-box-line", label: "录入", badge: 0 },
            { key: "history", icon: "ri-history-line", label: "历史", badge: 0 },
            { key: "profile", icon: "ri-user-line", label: "我的", badge: 0 },
          ].map(item => (
            <button
              key={item.key}
              onClick={() => {
                setTab(item.key as AppTab);
                if (item.key === "record" && pendingTasks[0]) {
                  handleStartTask(pendingTasks[0]);
                  setTab("tasks");
                }
              }}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 rounded-xl transition-colors cursor-pointer relative ${tab === item.key ? "text-[#E8531A]" : "text-gray-400"}`}
            >
              <i className={`${item.icon} text-xl`}></i>
              <span className="text-[10px]">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute -top-0.5 right-1 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <button onClick={() => navigate("/safety")} className="fixed top-4 left-4 bg-white border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
        <i className="ri-arrow-left-line mr-1"></i>返回系统
      </button>
    </div>
  );
}
